import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HiUser, HiMail, HiPhone, HiLocationMarker, HiLockClosed, HiEye, HiEyeOff } from 'react-icons/hi';
import PhoneInput from './PhoneInput';
import apiService from '../services/api';
import { apiConfig } from '../config/environment';
import './Profile.css';

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    nome: user?.nome || '',
    email: user?.email || '',
    telefone: user?.telefone || '',
    endereco: user?.endereco || '',
    senha_atual: '',
    nova_senha: '',
    confirmar_senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Função para construir URL completa do avatar
  const getAvatarUrl = useCallback((avatarPath) => {
    if (!avatarPath || !avatarPath.trim()) return '';

    // Se já é uma URL completa (começa com http), retorna como está
    if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
      return avatarPath;
    }

    // Normalizar o caminho: substituir barras invertidas por barras normais e remover espaços
    const normalizedPath = avatarPath.replace(/\\/g, '/').trim();

    // Remove barras duplicadas e constrói a URL completa
    const baseUrl = apiConfig.baseURL.replace(/\/$/, ''); // Remove barra final se existir
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
    return `${baseUrl}${cleanPath}`;
  }, []);

  // Carregar dados do perfil quando o componente montar
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        // Usar apiService.getProfile() diretamente
        await apiService.getProfile();
        // Atualizar o contexto após obter os dados
        if (refreshUser) {
          await refreshUser();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('❌ Erro ao carregar perfil:', err);
        setMessage({
          type: 'error',
          text: 'Erro ao carregar dados do perfil'
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [refreshUser]);

  // Atualizar formData quando user mudar
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        nome: user.nome || '',
        email: user.email || '',
        telefone: user.telefone || '',
        endereco: user.endereco || ''
      }));
    }
  }, [user]);

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Por favor, selecione uma imagem válida!' });
        return;
      }

      // Validar tamanho (máximo 12MB)
      if (file.size > 12 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'A imagem deve ter no máximo 12MB!' });
        return;
      }

      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result);
      };
      reader.readAsDataURL(file);
      setIsEditing(true);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Preparar dados para envio
      const profileData = {
        nome: formData.nome,
        telefone: formData.telefone,
        endereco: formData.endereco,
      };

      await apiService.updateProfile(profileData);

      // Se houver avatar, atualizar separadamente
      if (avatarFile) {
        await apiService.updateAvatar(avatarFile);
      }

      // Atualizar dados do usuário usando apiService.getProfile() diretamente
      await apiService.getProfile();
      if (refreshUser) {
        await refreshUser();
      }

      setIsEditing(false);
      setAvatarFile(null);
      setAvatarPreview(null);

      setMessage({ type: 'success', text: 'Perfil atualizado com sucesso!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Erro ao atualizar perfil:', err);
      setMessage({
        type: 'error',
        text: err.message || 'Erro ao atualizar perfil. Tente novamente.'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validações
    if (!formData.senha_atual || !formData.nova_senha || !formData.confirmar_senha) {
      setMessage({ type: 'error', text: 'Preencha todos os campos!' });
      return;
    }

    if (formData.nova_senha !== formData.confirmar_senha) {
      setMessage({ type: 'error', text: 'As senhas não coincidem!' });
      return;
    }

    if (formData.nova_senha.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres!' });
      return;
    }

    try {
      setLoadingPassword(true);
      setMessage({ type: '', text: '' });

      await apiService.changePassword({
        current_password: formData.senha_atual,
        password: formData.nova_senha,
        password_confirmation: formData.confirmar_senha
      });

      setMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);

      // Limpar campos de senha
      setFormData(prev => ({
        ...prev,
        senha_atual: '',
        nova_senha: '',
        confirmar_senha: ''
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('❌ Erro ao alterar senha:', err);
      setMessage({
        type: 'error',
        text: err.message || 'Erro ao alterar senha. Verifique se a senha atual está correta.'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Meu Perfil</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Meu Perfil</h1>
          <p>Gerencie suas informações pessoais</p>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                {isEditing && avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Preview do avatar"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : user?.avatar ? (
                  <img
                    src={getAvatarUrl(user.avatar)}
                    alt={`Avatar de ${user.nome}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling?.classList.remove('hidden');
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : null}
                <div
                  className={`avatar-placeholder ${user?.avatar || avatarPreview ? 'hidden' : ''}`}
                  style={{ display: user?.avatar || avatarPreview ? 'none' : 'flex' }}
                >
                  {user?.nome?.charAt(0).toUpperCase() || 'U'}
                </div>
              </div>
              <div className="avatar-badge">
                <HiUser size={14} />
              </div>
            </div>
            {isEditing && (
              <div className="avatar-upload" style={{ marginTop: '1rem', textAlign: 'center' }}>
                <label htmlFor="avatar-input" className="avatar-upload-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Trocar Foto
                </label>
                <input
                  id="avatar-input"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            <div className="profile-info">
              <h2>{user?.nome || 'Usuário'}</h2>
              <p>
                <HiMail size={16} />
                {user?.email || ''}
              </p>
            </div>
          </div>

          {!isEditing && (
            <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
              <button
                type="button"
                onClick={handleEditClick}
                className="btn btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Editar Perfil
              </button>
            </div>
          )}

          {isEditing && (
            <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1.25rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginRight: '0.5rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Cancelar
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <HiUser size={20} />
                </div>
                <h3>Informações Pessoais</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nome">
                    <HiUser size={18} />
                    Nome Completo
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="nome"
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Digite seu nome completo"
                      required
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    <HiMail size={18} />
                    Email
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      disabled
                      readOnly
                      placeholder="seu@email.com"
                      style={{ opacity: 0.7, cursor: 'not-allowed' }}
                    />
                  </div>
                  <small style={{ display: 'block', color: '#6c757d', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    O email não pode ser alterado
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telefone">
                    <HiPhone size={18} />
                    Telefone
                  </label>
                  <div className="input-wrapper">
                    <PhoneInput
                      id="telefone"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      placeholder="Número do telefone"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="endereco">
                    <HiLocationMarker size={18} />
                    Endereço
                  </label>
                  <div className="input-wrapper">
                    <input
                      id="endereco"
                      type="text"
                      value={formData.endereco}
                      onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                      placeholder="Rua, número, cidade, estado"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>
              {isEditing && (
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    <HiUser size={18} />
                    <span>{loading ? 'Salvando...' : 'Salvar Alterações'}</span>
                  </button>
                </div>
              )}
            </div>
          </form>

          <form onSubmit={handlePasswordChange} className="profile-form">
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <HiLockClosed size={20} />
                </div>
                <h3>Alterar Senha</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="senha_atual">Senha Atual</label>
                  <div className="password-input-container">
                    <input
                      id="senha_atual"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.senha_atual}
                      onChange={(e) => setFormData({ ...formData, senha_atual: e.target.value })}
                      placeholder="Digite sua senha atual"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nova_senha">Nova Senha</label>
                  <div className="password-input-container">
                    <input
                      id="nova_senha"
                      type={showNewPassword ? 'text' : 'password'}
                      value={formData.nova_senha}
                      onChange={(e) => setFormData({ ...formData, nova_senha: e.target.value })}
                      placeholder="Digite sua nova senha"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showNewPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="confirmar_senha">Confirmar Nova Senha</label>
                  <div className="password-input-container">
                    <input
                      id="confirmar_senha"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmar_senha}
                      onChange={(e) => setFormData({ ...formData, confirmar_senha: e.target.value })}
                      placeholder="Confirme sua nova senha"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showConfirmPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={loadingPassword}
                >
                  <HiLockClosed size={18} />
                  <span>{loadingPassword ? 'Alterando...' : 'Alterar Senha'}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;

